import BigNumber from 'bignumber.js';
import formatDate from 'date-fns/format';
import capitalize from 'lodash/capitalize';
import { Side } from '#/unified-rfqs/entities/shared';
import { BaseReq, PaginatedResp } from '@paradigm/api/src/fetch-api';
import { Nullable, UnixTimeMs } from '@paradigm/utils/types';
import { msToDate } from '@paradigm/utils/src/date';

import { getProduct, getActiveProducts } from '#/products/domain';
import { FiltersState } from '#/positions/contexts/filters/store';
import { formatProductQty } from '#/unified-rfqs/ui/decimal-format';
import { formatProductPrice } from '#/unified-rfqs/ui/rfqs/rfqs-functions';
import * as Greek from '@paradigm/features/src/unified-rfqs/entities/greeks';
import { FormattingFn, WithFormattingProps } from '#/unified-rfqs/ui/types';
import {
  DataProcessor,
  DataProcessorsByChannel,
  PositionsNotification,
  RawPositionsNotificationResp,
} from './types';

export interface RawGreeks {
  readonly delta: Nullable<string>;
  readonly gamma: Nullable<string>;
  readonly theta: Nullable<string>;
  readonly vega: Nullable<string>;
}

export interface Greeks {
  readonly delta: Nullable<BigNumber>;
  readonly gamma: Nullable<BigNumber>;
  readonly theta: Nullable<BigNumber>;
  readonly vega: Nullable<BigNumber>;
}

export interface RawPosition {
  readonly id: string;
  readonly account_name: string;
  readonly clearing_currency: string;
  readonly description: string;
  readonly expires_at: Nullable<UnixTimeMs>;
  readonly greeks: Nullable<RawGreeks>;
  readonly instrument_name: string;
  readonly mark_price: string;
  readonly product_code: string;
  readonly quantity: string;
  readonly side: Side;
  readonly unrealized_pnl: string;
  readonly venue_instrument_name: string;
  readonly venue: string;
  readonly option_kind: string;
  readonly strike: number;
}

export interface Position
  extends Omit<
    RawPosition,
    'expires_at' | 'greeks' | 'mark_price' | 'quantity' | 'unrealized_pnl'
  > {
  readonly expires_at: Nullable<Date>;
  readonly greeks: Nullable<Greeks>;
  readonly mark_price: BigNumber;
  readonly quantity: BigNumber;
  readonly unrealized_pnl: BigNumber;
}

export function processPosition(position: RawPosition): Position {
  return {
    ...position,
    expires_at:
      position.expires_at != null ? msToDate(position.expires_at) : null,
    greeks: position.greeks != null ? processGreeks(position.greeks) : null,
    mark_price: BigNumber(position.mark_price),
    quantity: BigNumber(position.quantity),
    unrealized_pnl: BigNumber(position.unrealized_pnl),
  };
}

function processGreeks(greeks: RawGreeks): Greeks {
  return {
    delta: greeks.delta != null ? BigNumber(greeks.delta) : null,
    gamma: greeks.gamma != null ? BigNumber(greeks.gamma) : null,
    theta: greeks.theta != null ? BigNumber(greeks.theta) : null,
    vega: greeks.vega != null ? BigNumber(greeks.vega) : null,
  };
}

export interface ListPositionsReq extends BaseReq {
  readonly cursor?: string | null;
  readonly pageSize?: number;
  readonly productCodes?: string[];
  readonly accounts?: string[];
  readonly venues?: string[];
}

export interface RawPositionResult {
  readonly positions: RawPosition[];
  readonly last_updated_at: UnixTimeMs;
}

export interface ListPositionsResult<T> extends PaginatedResp<T> {
  readonly last_updated_at: Date;
}

const dataProcessors: DataProcessorsByChannel = {
  position_updates(notif) {
    return {
      ...notif,
      positions: notif.positions.map(processPosition),
      last_updated_at: new Date(notif.last_updated_at),
    };
  },
};

export function processNotification(
  raw: RawPositionsNotificationResp,
): PositionsNotification {
  const { channel, data } = raw.params;
  const processor = dataProcessors[channel] as DataProcessor;
  return {
    params: {
      channel,
      data: processor(data),
    },
  } as PositionsNotification;
}

export interface PositionView
  extends Omit<
    Position,
    | 'quantity'
    | 'greeks'
    | 'product_code'
    | 'mark_price'
    | 'expires_at'
    | 'account_name'
    | 'unrealized_pnl'
  > {
  productCode: string;
  product: string;
  markPrice: string;
  expiresAt: WithFormattingProps<Position['expires_at']>;
  accountName: string;
  quantity: WithFormattingProps<BigNumber>;
  greeks: {
    delta: WithFormattingProps<Greeks['delta'] | undefined>;
    gamma: WithFormattingProps<Greeks['gamma'] | undefined>;
    theta: WithFormattingProps<Greeks['theta'] | undefined>;
    vega: WithFormattingProps<Greeks['vega'] | undefined>;
  };
  unrealizedPnl: {
    formatted: string;
    value: BigNumber;
    color: string;
    formattingFn: FormattingFn<BigNumber>;
    getColor: typeof getUnrealizedColor;
  };
}

const getUnrealizedColor = (value: BigNumber): string => {
  if (value.isGreaterThan(0)) return '#7ED321';
  if (value.isLessThan(0)) return '#EA135E';
  return '#ffffff';
};

const formatProductName: FormattingFn<string> = (value) => {
  const { currency, kind, venue, marginType } = getProduct(value);
  const margin = marginType === 'LINEAR' ? ' Linear' : '';
  return `${currency} ${capitalize(kind)}${margin} - ${venue}`;
};

const getProductByCode = (productCode: string) => getProduct(productCode);

function formatGreek(
  value: Nullable<BigNumber> | undefined,
  formattingFn: FormattingFn<Nullable<BigNumber> | undefined>,
) {
  return {
    formatted: formattingFn(value),
    value,
    formattingFn,
  };
}

export function processPositionView(position: Position): PositionView {
  const {
    expires_at,
    product_code,
    account_name,
    mark_price,
    unrealized_pnl,
    ...positionRest
  } = position;

  const product = getProductByCode(position.product_code);

  const clearing_currency =
    positionRest.clearing_currency || product.quoteCurrency || '';

  return {
    ...positionRest,
    clearing_currency,

    markPrice: formatProductPrice(mark_price.toString(), product.code),
    expiresAt: {
      formatted: formatExpiry(expires_at),
      value: expires_at,
      formattingFn: formatExpiry,
    },
    productCode: product_code,
    product: formatProductName(product_code),

    accountName: position.account_name,
    quantity: {
      formatted: formatProductQty(
        position.quantity.toString(),
        product.code,
        true,
      ),
      value: position.quantity,
      formattingFn: (value: BigNumber) =>
        formatProductQty(value.toString(), product.code, true),
    },
    unrealizedPnl: {
      formatted: formatProductPrice(
        position.unrealized_pnl.toString(),
        product.code,
      ),
      value: position.unrealized_pnl,
      formattingFn: (unrealizedPnl: BigNumber) =>
        formatProductPrice(unrealizedPnl.toString(), product.code),
      getColor: getUnrealizedColor,
      color: getUnrealizedColor(position.unrealized_pnl),
    },
    greeks: {
      delta: formatGreek(position.greeks?.delta, (value) =>
        Greek.formatDelta(value?.toString()),
      ),
      gamma: formatGreek(position.greeks?.gamma, (value) =>
        Greek.formatGamma(value?.toString()),
      ),
      theta: formatGreek(position.greeks?.theta, (value) =>
        Greek.formatTheta(value?.toString()),
      ),
      vega: formatGreek(position.greeks?.vega, (value) =>
        Greek.formatVega(value?.toString()),
      ),
    },
  };
}

function filterBy(filter: string[], value: string) {
  return filter.length === 0 ? true : filter.includes(value);
}

export function parseFiltersToProductCodes(filters: FiltersState): string[] {
  const activeProducts = getActiveProducts();
  const filteredProducts = activeProducts.filter(
    (product) =>
      filterBy(filters.products, product.code) &&
      filterBy(filters.assets, product.currency) &&
      filterBy(filters.instruments, product.kind) &&
      filterBy(filters.venues, product.venue),
  );
  const filteredProductCodes = filteredProducts.map((product) => product.code);
  const uniqueProductCodes = [...new Set(filteredProductCodes)];
  return uniqueProductCodes;
}

export function filterPositions(
  positions: ReadonlyArray<Position>,
  filters: FiltersState,
): ReadonlyArray<Position> {
  const productCodes = parseFiltersToProductCodes(filters);
  return positions.filter(
    (position) =>
      filterBy(filters.accounts, position.account_name) &&
      filterBy(productCodes, position.product_code),
  );
}

function formatExpiry(expires_at: Nullable<Date>): string {
  if (expires_at == null) return 'Perp';
  return formatDate(expires_at, 'dd MMM yy');
}
